import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' }
});

// Agrega token JWT en cada petición
api.interceptors.request.use(config => {
  const token = localStorage.getItem('pivasa_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Redirige a login si el token expira o es inválido
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401 && !error.config.url.includes('/auth/login')) {
      localStorage.removeItem('pivasa_token');
      localStorage.removeItem('pivasa_user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Dashboard
export const getDashboard = (businessId, { period, date } = {}) => {
  let url = `/api/dashboard?business_id=${businessId}`;
  if (period) url += `&period=${period}`;
  if (date)   url += `&date=${date}`;
  return api.get(url);
};

// Products
export const getProducts = (businessId, search = '', familyId = '') => {
  let url = `/api/products?business_id=${businessId}`;
  if (search) url += `&search=${search}`;
  if (familyId) url += `&family_id=${familyId}`;
  return api.get(url);
};

export const getProduct = (id, businessId) =>
  api.get(`/api/products/${id}?business_id=${businessId}`);

export const createProduct = (businessId, data) =>
  api.post(`/api/products?business_id=${businessId}`, data);

export const updateProduct = (id, businessId, data) =>
  api.put(`/api/products/${id}?business_id=${businessId}`, data);

export const deleteProduct = (id, businessId) =>
  api.delete(`/api/products/${id}?business_id=${businessId}`);

export const updateProductPrices = (id, businessId, data) =>
  api.patch(`/api/products/${id}/prices?business_id=${businessId}`, data);

// Presentaciones de venta
export const getProductPresentations = (productId, businessId) =>
  api.get(`/api/products/${productId}/presentations?business_id=${businessId}`);

export const createProductPresentation = (productId, businessId, data) =>
  api.post(`/api/products/${productId}/presentations?business_id=${businessId}`, data);

export const updateProductPresentation = (productId, presentationId, businessId, data) =>
  api.put(`/api/products/${productId}/presentations/${presentationId}?business_id=${businessId}`, data);

export const deleteProductPresentation = (productId, presentationId, businessId) =>
  api.delete(`/api/products/${productId}/presentations/${presentationId}?business_id=${businessId}`);

// Clients
export const getClients = (businessId, filters = {}) => {
  let url = `/api/clients?business_id=${businessId}`;
  if (filters.search)        url += `&search=${encodeURIComponent(filters.search)}`;
  if (filters.document_type) url += `&document_type=${encodeURIComponent(filters.document_type)}`;
  if (filters.document_number) url += `&document_number=${encodeURIComponent(filters.document_number)}`;
  if (filters.name)          url += `&name=${encodeURIComponent(filters.name)}`;
  return api.get(url);
};

export const getClient = (id, businessId) =>
  api.get(`/api/clients/${id}?business_id=${businessId}`);

export const createClient = (businessId, data) =>
  api.post(`/api/clients?business_id=${businessId}`, data);

export const updateClient = (id, businessId, data) =>
  api.put(`/api/clients/${id}?business_id=${businessId}`, data);

export const deleteClient = (id, businessId) =>
  api.delete(`/api/clients/${id}?business_id=${businessId}`);

// Providers
export const getProviders = (businessId, search = '') => {
  let url = `/api/providers?business_id=${businessId}`;
  if (search) url += `&search=${search}`;
  return api.get(url);
};

export const getProvider = (id, businessId) =>
  api.get(`/api/providers/${id}?business_id=${businessId}`);

export const createProvider = (businessId, data) =>
  api.post(`/api/providers?business_id=${businessId}`, data);

export const updateProvider = (id, businessId, data) =>
  api.put(`/api/providers/${id}?business_id=${businessId}`, data);

export const deleteProvider = (id, businessId) =>
  api.delete(`/api/providers/${id}?business_id=${businessId}`);

export const getProvidersWithDebt = (businessId) =>
  api.get(`/api/providers/with-debt?business_id=${businessId}`);

// Families (Clase o Familia de Productos)
export const getFamilies = (businessId, search = '') => {
  let url = `/api/families?business_id=${businessId}`;
  if (search) url += `&search=${encodeURIComponent(search)}`;
  return api.get(url);
};

export const createFamily = (businessId, data) =>
  api.post(`/api/families?business_id=${businessId}`, data);

export const updateFamily = (id, businessId, data) =>
  api.put(`/api/families/${id}?business_id=${businessId}`, data);

export const deleteFamily = (id, businessId) =>
  api.delete(`/api/families/${id}?business_id=${businessId}`);

// Warehouses (Almacenes)
export const getWarehouses = (businessId) =>
  api.get(`/api/warehouses?business_id=${businessId}`);

export const createWarehouse = (businessId, data) =>
  api.post(`/api/warehouses?business_id=${businessId}`, data);

export const updateWarehouse = (id, businessId, data) =>
  api.put(`/api/warehouses/${id}?business_id=${businessId}`, data);

export const deleteWarehouse = (id, businessId) =>
  api.delete(`/api/warehouses/${id}?business_id=${businessId}`);

export const getWarehouseStock = (warehouseId, businessId, params = {}) => {
  let url = `/api/warehouses/${warehouseId}/stock?business_id=${businessId}`;
  if (params.search)    url += `&search=${encodeURIComponent(params.search)}`;
  if (params.family_id) url += `&family_id=${params.family_id}`;
  return api.get(url);
};

export const setWarehouseInitialStock = (warehouseId, productId, businessId, data) =>
  api.post(`/api/warehouses/${warehouseId}/stock/${productId}/initial?business_id=${businessId}`, data);

// Movements (Movimientos de Inventario)
export const getMovements = (params = {}) => {
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '' && v !== null)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');
  // Si se pide kardex, usar el endpoint de kardex
  if (params.kardex) {
    const base = qs.replace(/&?kardex=true/, '').replace(/^kardex=true&?/, '');
    return api.get(`/api/movements/kardex?${base}`);
  }
  return api.get(`/api/movements?${qs}`);
};

export const createMovement = (businessId, data) =>
  api.post(`/api/movements?business_id=${businessId}`, data);

export const anularMovement = (id, businessId) =>
  api.patch(`/api/movements/${id}/anular?business_id=${businessId}`);

// Datos de empresa (para exportaciones)
export const getEmpresaInfo = (businessId) =>
  api.get(`/api/config/empresa?business_id=${businessId}`);

// Tipos de movimiento (causas) — applies_to: 'ingreso' | 'salida' | 'ambos' | undefined (todos)
export const getMovementCauseTypes = (businessId, applies_to = '', manualOnly = false) => {
  let url = `/api/config/tipos-movimiento?business_id=${businessId}`;
  if (applies_to)  url += `&applies_to=${applies_to}`;
  if (manualOnly)  url += `&manual_only=true`;
  return api.get(url);
};

// Conversions (Conversiones de inventario)
export const getConversions = (businessId, params = {}) => {
  let url = `/api/conversions?business_id=${businessId}`;
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '' && v !== null) url += `&${k}=${encodeURIComponent(v)}`;
  });
  return api.get(url);
};

export const createConversion = (businessId, data) =>
  api.post(`/api/conversions?business_id=${businessId}`, data);

export const getConversionById = (id, businessId) =>
  api.get(`/api/conversions/${id}?business_id=${businessId}`);

export const anularConversion = (id, businessId) =>
  api.patch(`/api/conversions/${id}/anular?business_id=${businessId}`);

// Transfers (Transferencias de inventario)
export const getTransfers = (businessId, params = {}) => {
  let url = `/api/transfers?business_id=${businessId}`;
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '' && v !== null) url += `&${k}=${encodeURIComponent(v)}`;
  });
  return api.get(url);
};

export const createTransfer = (businessId, data) =>
  api.post(`/api/transfers?business_id=${businessId}`, data);

export const getTransferById = (id, businessId) =>
  api.get(`/api/transfers/${id}?business_id=${businessId}`);

export const anularTransfer = (id, businessId) =>
  api.patch(`/api/transfers/${id}/anular?business_id=${businessId}`);

// Cuentas por Pagar
export const getCxPSummary = (businessId, { from_date, to_date } = {}) => {
  let url = `/api/cuentas-pagar/summary?business_id=${businessId}`;
  if (from_date) url += `&from_date=${encodeURIComponent(from_date)}`;
  if (to_date)   url += `&to_date=${encodeURIComponent(to_date)}`;
  return api.get(url);
};

export const getCxPList = (businessId, params = {}) => {
  let url = `/api/cuentas-pagar?business_id=${businessId}`;
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '' && v !== null) url += `&${k}=${encodeURIComponent(v)}`;
  });
  return api.get(url);
};

export const getCxPById = (id, businessId) =>
  api.get(`/api/cuentas-pagar/${id}?business_id=${businessId}`);

export const pagarCxP = (id, businessId, data) =>
  api.post(`/api/cuentas-pagar/${id}/pagar?business_id=${businessId}`, data);

export const revertirPagoCxP = (id, paymentId, businessId) =>
  api.delete(`/api/cuentas-pagar/${id}/pagos/${paymentId}?business_id=${businessId}`);

// Cuentas por Cobrar
export const getCxCSummary = (businessId, { from_date, to_date } = {}) => {
  let url = `/api/cuentas-cobrar/summary?business_id=${businessId}`;
  if (from_date) url += `&from_date=${encodeURIComponent(from_date)}`;
  if (to_date)   url += `&to_date=${encodeURIComponent(to_date)}`;
  return api.get(url);
};

export const getCxCList = (businessId, params = {}) => {
  let url = `/api/cuentas-cobrar?business_id=${businessId}`;
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '' && v !== null) url += `&${k}=${encodeURIComponent(v)}`;
  });
  return api.get(url);
};

export const getCxCById = (id, businessId) =>
  api.get(`/api/cuentas-cobrar/${id}?business_id=${businessId}`);

export const cobrarCxC = (id, businessId, data) =>
  api.post(`/api/cuentas-cobrar/${id}/cobrar?business_id=${businessId}`, data);

export const cobrarMultipleCxC = (businessId, data) =>
  api.post(`/api/cuentas-cobrar/cobrar-multiple?business_id=${businessId}`, data);

// Anula la cobranza COMPLETA (cabecera + todas sus ventas) — `cobranzaId` es
// el id de la cabecera (antes recibía ar_id + payment_id de una sola línea).
export const revertirCobroCxC = (cobranzaId, businessId) =>
  api.delete(`/api/cobranzas/${cobranzaId}?business_id=${businessId}`);

// Cobranzas
export const getCobranzasPagos = (businessId, params = {}) => {
  let url = `/api/cobranzas/pagos?business_id=${businessId}`;
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '' && v !== null) url += `&${k}=${encodeURIComponent(v)}`;
  });
  return api.get(url);
};

export const getEstadoCuentaCliente = (clientId, businessId) =>
  api.get(`/api/cobranzas/clientes/${clientId}/estado-cuenta?business_id=${businessId}`);

// Purchases (Compras)
export const getPurchases = (businessId, params = {}) => {
  let url = `/api/purchases?business_id=${businessId}`;
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '' && v !== null) url += `&${k}=${encodeURIComponent(v)}`;
  });
  return api.get(url);
};

export const getPurchase = (id, businessId) =>
  api.get(`/api/purchases/${id}?business_id=${businessId}`);

export const createPurchase = (businessId, data) =>
  api.post(`/api/purchases?business_id=${businessId}`, data);

export const checkPurchaseBlockers = (id, businessId) =>
  api.get(`/api/purchases/${id}/blockers?business_id=${businessId}`);

export const anularPurchase = (id, businessId) =>
  api.patch(`/api/purchases/${id}/anular?business_id=${businessId}`);

// Stock (Inventarios)
export const getStockSummary = (businessId) =>
  api.get(`/api/stocks/summary?business_id=${businessId}`);

export const getStockByWarehouse = (businessId, params = {}) => {
  let url = `/api/stocks/by-warehouse?business_id=${businessId}`;
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '' && v !== null) url += `&${k}=${encodeURIComponent(v)}`;
  });
  return api.get(url);
};

export const getStockByProduct = (businessId, params = {}) => {
  let url = `/api/stocks/by-product?business_id=${businessId}`;
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '' && v !== null) url += `&${k}=${encodeURIComponent(v)}`;
  });
  return api.get(url);
};

export const getStockMovementsSummary = (businessId, params = {}) => {
  let url = `/api/stocks/movements-summary?business_id=${businessId}`;
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '' && v !== null) url += `&${k}=${encodeURIComponent(v)}`;
  });
  return api.get(url);
};

// Config
export const getConfig = (path, businessId) =>
  api.get(`/api/config/${path}?business_id=${businessId}`);

export const createConfig = (path, businessId, data) =>
  api.post(`/api/config/${path}?business_id=${businessId}`, data);

export const updateConfig = (path, id, businessId, data) =>
  api.put(`/api/config/${path}/${id}?business_id=${businessId}`, data);

export const deleteConfig = (path, id, businessId) =>
  api.delete(`/api/config/${path}/${id}?business_id=${businessId}`);

// ── Empresas (superadmin) ──────────────────────────────────────────────────
export const getBusinesses = () =>
  api.get('/api/businesses');

export const getBusiness = (id) =>
  api.get(`/api/businesses/${id}`);

export const createBusiness = (formData) =>
  api.post('/api/businesses', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

export const updateBusiness = (id, formData) =>
  api.put(`/api/businesses/${id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });

export const toggleBusinessStatus = (id) =>
  api.patch(`/api/businesses/${id}/toggle-status`);

// ── Cajas (cash_desks) — creadas y asignadas por el administrador ──────────
export const getCashDesks = () =>
  api.get('/api/cash-desks');

export const createCashDesk = (data) =>
  api.post('/api/cash-desks', data);

export const updateCashDesk = (id, data) =>
  api.put(`/api/cash-desks/${id}`, data);

export const toggleCashDeskActive = (id) =>
  api.patch(`/api/cash-desks/${id}/active`);

// ── Caja (sesiones de apertura/cierre, amarradas a una Caja/cash_desk) ─────
// Sesión abierta del usuario autenticado (cualquier Caja) — fuente de verdad
// para Ventas, filtrada server-side por el JWT (no confiar en localStorage solo).
export const getMyOpenCashRegister = () =>
  api.get('/api/cash-registers/mine');

export const getCashRegisterCurrent = (cajaId) =>
  api.get(`/api/cash-registers/current?caja_id=${cajaId}`);

export const getCashRegisterLatest = (cajaId) =>
  api.get(`/api/cash-registers/latest?caja_id=${cajaId}`);

// Todas las cajas abiertas ahora mismo, de cualquier usuario — solo admin
export const getAllOpenCashRegisters = () =>
  api.get('/api/cash-registers/all-open');

export const reopenCashRegister = (id) =>
  api.patch(`/api/cash-registers/${id}/reopen`);

export const openCashRegister = (data) =>
  api.post('/api/cash-registers/open', data);

export const getCashRegisterSummary = (id) =>
  api.get(`/api/cash-registers/${id}/summary`);

export const closeCashRegister = (id, data) =>
  api.post(`/api/cash-registers/${id}/close`, data);

export const getCashRegisterMovements = (id, type) => {
  let url = `/api/cash-registers/${id}/movements`;
  if (type) url += `?type=${type}`;
  return api.get(url);
};

export const addCashRegisterMovement = (id, data) =>
  api.post(`/api/cash-registers/${id}/movements`, data);

export const getCashRegisterHistory = (params = {}) => {
  let url = '/api/cash-registers/history';
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '' && v !== null)
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');
  if (qs) url += `?${qs}`;
  return api.get(url);
};

// ── Ventas ────────────────────────────────────────────────────────────────
export const getSales = (businessId, filters = {}) => {
  let url = `/api/sales?business_id=${businessId}`;
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== '' && v !== null) url += `&${k}=${encodeURIComponent(v)}`;
  });
  return api.get(url);
};

export const getSale = (id, businessId) =>
  api.get(`/api/sales/${id}?business_id=${businessId}`);

export const createSale = (businessId, data) =>
  api.post(`/api/sales?business_id=${businessId}`, data);

export const anularVenta = (id, businessId) =>
  api.patch(`/api/sales/${id}/anular?business_id=${businessId}`);

export const getSalesSeries = (businessId) =>
  api.get(`/api/sales/series?business_id=${businessId}`);

export const getSalesStats = (businessId, filters = {}) => {
  let url = `/api/sales/stats?business_id=${businessId}`;
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== '') url += `&${k}=${encodeURIComponent(v)}`;
  });
  return api.get(url);
};

// ── Tipo de cambio y búsqueda DNI/RUC ─────────────────────────────────────
export const getExchangeRateToday = (currencyCode = 'USD') =>
  api.get(`/api/exchange/today?currency=${currencyCode}`);

export const getExchangeRateTodayAll = () =>
  api.get('/api/exchange/today-all');

export const fetchExchangeRateFromAPI = () =>
  api.get('/api/exchange/fetch-api');

export const saveExchangeRate = (data) =>
  api.post('/api/exchange/save', data);

export const getExchangeCurrencies = () =>
  api.get('/api/exchange/currencies');

export const lookupDocument = (type, number) =>
  api.get(`/api/exchange/lookup?type=${type}&number=${encodeURIComponent(number)}`);

// ── Reportes ──────────────────────────────────────────────────────────────
function reporteUrl(path, businessId, params = {}) {
  let url = `/api/reportes/${path}?business_id=${businessId}`;
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '' && v !== null) url += `&${k}=${encodeURIComponent(v)}`;
  });
  return url;
}

export const getReporteVentas = (businessId, params = {}) =>
  api.get(reporteUrl('ventas', businessId, params));

export const getReporteCompras = (businessId, params = {}) =>
  api.get(reporteUrl('compras', businessId, params));

export const getReporteUtilidades = (businessId, params = {}) =>
  api.get(reporteUrl('utilidades', businessId, params));

export const getReporteProductos = (businessId, params = {}) =>
  api.get(reporteUrl('productos', businessId, params));

export const getReporteAlmacenVentas = (businessId, params = {}) =>
  api.get(reporteUrl('almacen-ventas', businessId, params));

export const getReporteCuentasCobrar = (businessId, params = {}) =>
  api.get(reporteUrl('cuentas-cobrar', businessId, params));

export const getReporteCuentasPagar = (businessId, params = {}) =>
  api.get(reporteUrl('cuentas-pagar', businessId, params));

export const getReporteCobranzas = (businessId, params = {}) =>
  api.get(reporteUrl('cobranzas', businessId, params));

export default api;
